import React, { useEffect, useState } from "react";
import { Radio, Timer, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * UrgencyBadge — renders a coloured chip based on server-configured
 * thresholds. NEVER hardcode the 15/60 minute cutoff — always read
 * `/api/live-world/urgency-config` and let the server decide.
 *
 * Props:
 *   urgency?: "live" | "ending_soon" | "ending_very_soon" | "closed"
 *     — if provided, we render directly.
 *   closes_at?: ISO string
 *     — if provided (and urgency is not), we compute locally against
 *       the server's thresholds. This keeps the countdown live-ticking
 *       without a re-fetch after the initial state.
 *   compact?: boolean — smaller variant for grid cards.
 */

let _thresholdCache = null;

async function getThresholds() {
  if (_thresholdCache) return _thresholdCache;
  try {
    const r = await api.get("/live-world/urgency-config");
    _thresholdCache = r.data;
    return _thresholdCache;
  } catch {
    return {
      thresholds: { ending_very_soon_min: 15, ending_soon_min: 60 },
      labels:     { live: "Live", ending_soon: "Ending soon", ending_very_soon: "Ending very soon", closed: "Closed" },
      colors:     { live: "#059669", ending_soon: "#f59e0b", ending_very_soon: "#dc2626", closed: "#71717a" },
    };
  }
}

function urgencyFor(closesAt, thresholds) {
  if (!closesAt) return "live";
  const closes = new Date(closesAt);
  if (isNaN(closes)) return "live";
  const mins = (closes - new Date()) / 60_000;
  if (mins <= 0) return "closed";
  if (mins <= thresholds.ending_very_soon_min) return "ending_very_soon";
  if (mins <= thresholds.ending_soon_min) return "ending_soon";
  return "live";
}

function minsLeft(closesAt) {
  if (!closesAt) return null;
  const ms = new Date(closesAt) - new Date();
  if (ms <= 0) return 0;
  return Math.floor(ms / 60_000);
}

export default function UrgencyBadge({ urgency, closes_at, compact = false }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    getThresholds().then(setCfg);
    // Re-render every 30s so the badge transitions from live → soon → very-soon
    if (closes_at && !urgency) {
      const t = setInterval(() => setCfg((c) => (c ? { ...c } : c)), 30_000);
      return () => clearInterval(t);
    }
  }, [closes_at, urgency]);
  if (!cfg) return null;

  const state = urgency || urgencyFor(closes_at, cfg.thresholds);
  const color = cfg.colors[state] || cfg.colors.live;
  const label = cfg.labels[state] || cfg.labels.live;
  const mins  = closes_at ? minsLeft(closes_at) : null;

  // Icon by state
  const Ic = state === "ending_very_soon" ? AlertTriangle
           : state === "ending_soon"      ? Timer
           : state === "closed"           ? Timer
           :                                Radio;

  const size = compact ? 9 : 10;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full font-black text-white ${
        compact ? "px-1.5 py-0.5" : "px-2 py-0.5"
      }`}
      style={{ background: color, fontSize: compact ? 9 : 10 }}
      data-testid={`urgency-${state}`}
      title={label + (mins != null ? ` · ${mins}m left` : "")}
    >
      <Ic size={size} className={state === "ending_very_soon" ? "animate-pulse" : ""}/>
      {mins != null && state !== "closed" && state !== "live"
        ? `${mins}m`
        : label.toUpperCase()}
    </span>
  );
}
